import { Modal } from 'bootstrap';

import { getApiUrl } from '@/js/utils/getApiUrl';
import { getMockResident, type MockResidentData } from '@/js/utils/get-mock-resident';

interface ResidentData {
  image: string;
  name: string;
  description: string;
  url?: string | null;
  success?: boolean;
  msg?: string;
}

let clusterResidentModal: ResidentModalWidget | null = null;

export function getClusterResidentModal(): ResidentModalWidget {
  if (!clusterResidentModal) {
    clusterResidentModal = new ResidentModalWidget(true);
  }
  return clusterResidentModal;
}

export class ResidentModalWidget {
  private modalElement: HTMLElement | null = null;
  private modalContent: HTMLElement | null = null;
  private modal: Modal | null = null;
  private useMockFallback: boolean;

  constructor(useMockFallback = false) {
    this.useMockFallback = useMockFallback;
  }

  public init(): void {
    this.modalElement = document.getElementById('residentModal');
    this.modalContent = document.getElementById('residentModalContent');

    if (this.modalElement) {
      this.mountModalToBody();
      this.modal = Modal.getOrCreateInstance(this.modalElement);
    }

    this.bindEvents();
  }

  private mountModalToBody(): void {
    if (!this.modalElement || this.modalElement.parentElement === document.body) return;
    document.body.appendChild(this.modalElement);
  }

  private bindEvents(): void {
    const residentCards = document.querySelectorAll('.resident-card[data-id]');
    residentCards.forEach((card) => {
      const el = card as HTMLElement;
      if (el.closest('.edge-cards-carousel__source')) return;
      if (el.dataset.residentBound) return;
      el.dataset.residentBound = '1';
      el.addEventListener('click', (event: Event) => {
        const target = event.currentTarget as HTMLElement;
        if (target.getAttribute('data-id')) {
          this.openResidentModalFromCard(target);
        }
      });
      el.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.openResidentModalFromCard(el);
      });
    });

    if (this.modalElement && !this.modalElement.dataset.residentModalBound) {
      this.modalElement.dataset.residentModalBound = '1';
      this.modalElement.addEventListener('show.bs.modal', (event: Event) => {
        const related = (event as unknown as { relatedTarget: HTMLElement | null }).relatedTarget;
        if (!related) {
          return;
        }

        document.dispatchEvent(
          new CustomEvent('residentModal:autoplayStop'),
        );

        const residentId =
          related.getAttribute('data-id') ?? related.getAttribute('data-resident-id');
        if (residentId) {
          this.loadResidentData(residentId);
        }
      });

      this.modalElement.addEventListener('hidden.bs.modal', () => {
        document.dispatchEvent(
          new CustomEvent('residentModal:autoplayStart'),
        );

        this.resetModalContent();
      });
    }
  }

  private openResidentModalFromCard(card: HTMLElement): void {
    if (!this.modal) {
      return;
    }
    this.modal.show(card);
  }

  private loadResidentData(residentId: string): void {
    if (!this.modalContent) return;

    this.showLoadingState();

    fetch(getApiUrl(`/api/getResident.php?id=${encodeURIComponent(residentId)}`))
      .then((response) => {
        if (!response.ok) throw new Error();
        return response.json() as Promise<ResidentData>;
      })
      .then((data) => {
        if (data.success === false || !data.name) throw new Error();
        this.displayResidentData(data);
      })
      .catch(() => {
        if (this.useMockFallback) {
          const mock = getMockResident(residentId);
          if (mock) {
            this.displayResidentData(mock);
            return;
          }
        }
        this.showErrorState();
      });
  }

  private showLoadingState(): void {
    if (!this.modalContent) return;

    this.modalContent.innerHTML = `
            <div class="loading-spinner">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Загрузка...</span>
                </div>
                <span class="ms-2">Загрузка данных резидента...</span>
            </div>
        `;
  }

  private hostnameFromUrl(url: string): string {
    try {
      const { hostname } = new URL(url, window.location.href);
      return hostname || url;
    } catch {
      return url;
    }
  }

  private displayResidentData(data: ResidentData | MockResidentData): void {
    if (!this.modalContent) return;

    const residentUrl = data.url;
    let urlLinkBlock = '';
    if (residentUrl) {
      const domain = this.hostnameFromUrl(residentUrl);
      urlLinkBlock = `<div class="mt-2">
            <a href="${residentUrl}" target="_blank" rel="noopener noreferrer">${domain}</a>
        </div>`;
    }

    this.modalContent.innerHTML = `
    <div class="resident-modal-content">
        <div class="resident-image-container">
            <img src="${data.image}" alt="${data.name}" class="rounded">
        </div>
        <div class="resident-details">
            <div class="resident-details-header">
              <h4 class="resident-name">${data.name}</h4>
              ${urlLinkBlock}
            </div>
            <span class="resident-description-separator">О компании</span>
            <div class="resident-description">${data.description}</div>
        </div>
    </div>
`;
  }

  private showErrorState(): void {
    if (!this.modalContent) return;

    this.modalContent.innerHTML = `
    <div class="error-content w-100 text-center p-4">
      <p class="mb-0">Не удалось загрузить данные.</p>
    </div>
  `;
  }

  private resetModalContent(): void {
    if (!this.modalContent) return;

    this.modalContent.innerHTML = `
            <div class="loading-spinner">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Загрузка...</span>
                </div>
                <span class="ms-2">Загрузка данных резидента...</span>
            </div>
        `;
  }
}
